/**
 * BottomNav
 * =========
 * Mobile-first sticky bottom navigation.
 * Active tab tracked in UIStore. Notification dot from React Query.
 */

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { colors } from "@/theme/tokens";
import { useUIStore } from "@/store/uiStore";
import { useAuth } from "@/hooks/useAuth";
import { useNotifications } from "@/hooks/useRequests";

const TABS = [
  { id: "home"     as const, label:"Home",     icon:"🏠", path:"/"          },
  { id: "search"   as const, label:"Search",   icon:"🔍", path:"/search"    },
  { id: "shelf"    as const, label:"Shelf",    icon:"📚", path:"/my-shelf"  },
  { id: "requests" as const, label:"Requests", icon:"🔔", path:"/requests", protected:true },
  { id: "profile"  as const, label:"Profile",  icon:"👤", path:"/profile",  protected:true },
];

export const BottomNav: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { showModal }   = useUIStore();
  const { isSignedIn, uid }  = useAuth();
  const { unreadCount } = useNotifications(uid);

  const currentTab = TABS.find((t) => {
    if (t.path === "/") return location.pathname === "/";
    return location.pathname.startsWith(t.path);
  })?.id ?? "home";

  const handleTap = (tab: typeof TABS[0]) => {
    if (tab.protected && !isSignedIn) {
      showModal("signIn");
      return;
    }
    navigate(tab.path);
  };

  return (
    <nav
      className="bottom-nav"
      style={{
        position:   "fixed", bottom:0, left:0, right:0,
        height:     60,
        background: "#fff",
        borderTop:  `1px solid ${colors.border}`,
        display:    "flex",
        zIndex:     90,
      }}
    >
      {TABS.map((tab) => {
        const active = currentTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => handleTap(tab)}
            style={{
              flex:1, border:"none", background:"none", cursor:"pointer",
              display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", gap:3, padding:"8px 0",
              position:"relative",
            }}
            aria-label={tab.label}
            aria-current={active ? "page" : undefined}
          >
            <span style={{ fontSize:20, lineHeight:1 }}>{tab.icon}</span>
            <span style={{
              fontSize:9, fontWeight: active ? 800 : 600, letterSpacing:"0.03em",
              color: active ? colors.primary : colors.textMuted,
              fontFamily:"var(--font-body)",
            }}>
              {tab.label}
            </span>
            {active && (
              <div style={{
                position:"absolute", bottom:0, left:"50%",
                transform:"translateX(-50%)",
                width:22, height:3, borderRadius:"2px 2px 0 0",
                background: colors.primary,
              }}/>
            )}
            {/* Notification dot — requests tab only, real count */}
            {tab.id === "requests" && unreadCount > 0 && (
              <div style={{
                position:"absolute", top:6, right:"calc(50% - 14px)",
                width:7, height:7,
                background: colors.error, borderRadius:"50%",
                border:"2px solid #fff",
              }}/>
            )}
          </button>
        );
      })}
    </nav>
  );
};
